import Button from "Components/Button";
import FlexRow from "Components/FlexRow";
import Modal from "Components/Modal";
import Space from "Components/Space";
import {useTranslation} from "react-i18next";
import {useCustomerOrderNotificationPreview, useSendCustomerOrderNotification} from "../../../Apis/InvoicesApi";
import {CustomerOrderNotificationType, IOrderDto} from "../../../Apis/Models/IOrderDto";
import useToasts from "../../../Hooks/UseToasts";
import {Controller, useForm, useWatch} from "react-hook-form";
import styles from './SendCustomerOrderNotificationModal.module.scss';
import ControlledInput from "../../../Components/Input/ControlledInput";
import ControlledTextArea from "../../../Components/TextArea/ControlledTextArea";
import Label from "../../../Components/Label";
import CheckmarkIcon from "../../../Assets/SVG/CheckmarkIcon";
import XIcon from "../../../Assets/SVG/XIcon";

type FormData = {
    email: string;
    phone: string;
    emailNotification: boolean;
    phoneNotification: boolean;
    message: string;
    subject: string;
}

interface Props {
    visible: boolean;
    toggleVisible: () => void;
    onSuccess?: () => void;
    order: IOrderDto;
    customerOrderNotificationType: CustomerOrderNotificationType
}
const SendCustomerOrderNotificationModal = (props: Props) => {

    const { t } = useTranslation();

    const { data: notificationPreview } = useCustomerOrderNotificationPreview(
        props.order.id,
        props.customerOrderNotificationType,
        props.visible
    );

    const { showSuccessToast, showErrorToast } = useToasts();
    const { control, handleSubmit, reset } = useForm<FormData>({
        defaultValues: {
            email: props.order.customer.email,
            phone: props.order.customer.phone,
            emailNotification: false,
            phoneNotification: false,
            subject: notificationPreview?.subject,
            message: notificationPreview?.message,
        }
    });


    const emailNotification = useWatch({
        control,
        name: "emailNotification",
    });

    const phoneNotification = useWatch({
        control,
        name: "phoneNotification",
    });

    const { mutateAsync, isLoading } = useSendCustomerOrderNotification();

    const onSubmit = async (data: FormData) => {
        try {

            await mutateAsync({
                orderId: props.order.id,
                type: props.customerOrderNotificationType,
                emailNotification: data.emailNotification,
                phoneNotification: data.phoneNotification,
                notificationMessage: data.message,
                notificationSubject: data.subject,
                recipientEmail: data.email ? [data.email] : null,
                recipientPhone: data.phone ? [data.phone] : null,
            });

            reset({})

            showSuccessToast(t('customerNotificationSend'));

            props.toggleVisible();
            props.onSuccess && props.onSuccess();
        }
        catch (e) {
            showErrorToast(t('anErrorOccurred'))
        }
    }

    const getNotificationText = () => {
        switch (props.customerOrderNotificationType) {
            case CustomerOrderNotificationType.ProductionCompleted:
                return t('customerNotificationType.ProductionCompleted');
            case CustomerOrderNotificationType.OrderAccepted:
                return t('customerNotificationType.OrderAccepted');
        }
    }

    return (
        <Modal title={t('sendCustomerOrderNotificationTitle')} subTitle={getNotificationText()} visible={props.visible} onCancel={() => props.toggleVisible()} modalStyle={{overflow: 'visible'}}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Space size={"large"}>
                    <Space size={"small"}>
                        <div className={`${styles.notificationTypeHeader}`}>
                            {t('chooseNotificationType')}:
                        </div>
                        <FlexRow direction={"row"} style={{gap: 15}} justify={"start"} align={"start"}>
                            <Controller control={control} name={"emailNotification"} render={({field}) => (
                                <div onClick={() => field.onChange(!field.value)}
                                     className={"flex column clickable"} style={{width: 'min-content'}}>
                                    <Label placeholder={t('recipientEmail')}/>
                                    <div className={"input"} style={{
                                        width: 'min-content',
                                        padding: 10,
                                        backgroundColor: '#FCFCFC',
                                        borderRadius: 3,
                                        borderWidth: 1,
                                        borderColor: '#EADEDE',
                                        borderStyle: 'solid',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        {field.value ? <CheckmarkIcon height={20} width={20}/> :
                                            <XIcon height={20} width={20}/>}
                                    </div>
                                </div>
                            )}/>

                            <Controller control={control} name={"phoneNotification"} render={({field}) => (
                            <div onClick={() => field.onChange(!field.value)}
                                 className={"flex column clickable"} style={{width: 'min-content'}}>
                                <Label placeholder={t('notificationTypeSms')}/>
                                <div className={"input"} style={{
                                    width: 'min-content',
                                    padding: 10,
                                    backgroundColor: '#FCFCFC',
                                    borderRadius: 3,
                                    borderWidth: 1,
                                    borderColor: '#EADEDE',
                                    borderStyle: 'solid',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    {field.value ? <CheckmarkIcon height={20} width={20}/> :
                                        <XIcon height={20} width={20}/>}
                                </div>
                            </div>
                        )}/>
                        </FlexRow>
                    </Space>

                    {emailNotification && (
                        <ControlledInput rules={{required: true}} type={"email"} name={"email"} control={control} placeholder={t('RecipientEmail')} defaultValue={props.order.customer.email}/>
                    )}

                    {phoneNotification && (
                        <ControlledInput rules={{required: true, minLength: 8, maxLength: 8}} type={"phone"} name={"phone"} control={control} placeholder={t('recipientPhone')} defaultValue={props.order.customer.phone}/>
                    )}

                    {(emailNotification || phoneNotification) && (
                        <>
                            <div className={`${styles.notificationTypeHeader}`}>
                                {t('notificationPreview')}
                            </div>
                            <FlexRow direction={"column"} align={"start"}>
                                <ControlledInput type={"subject"} name={"subject"} control={control} placeholder={`${t('notificationSubject')}:`} defaultValue={notificationPreview?.subject}/>
                                <div className={`${styles.fieldHeader}`} style={{paddingTop: 10}}>
                                    {t('notificationMessage')}
                                </div>
                                <ControlledTextArea name={"message"} control={control} rows={2} placeholder={t('notificationMessage')} defaultValue={notificationPreview?.message}/>
                            </FlexRow>

                            <FlexRow justify={"end"}>
                                <Button type={"secondary"} buttonType={"submit"} loading={isLoading} text={t('send')}/>
                            </FlexRow>
                        </>
                    )}
                </Space>
            </form>
        </Modal>
    )
}

export default SendCustomerOrderNotificationModal